import { Calendar, RefreshCcw, Search, Wallet } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp } from "../../../services/apiService";

type HotspotPayment = {
  id: number;
  transaction_code: string;
  phone_number: string;
  amount: number;
  status: string;
  created_at: string;
  user: { username: string };
  plan: { name: string };
};

const HotspotPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<HotspotPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const data = await fetchApp(`${API_CONFIG.baseUrl}/hotspot-payment/list`);
      setPayments(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const filteredPayments = useMemo(() => {
    return payments.filter((p) => {
      const matchesSearch =
        p.transaction_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone_number.includes(searchTerm) ||
        p.user?.username.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [payments, searchTerm, statusFilter]);

  const StatusBadge = ({ status }: { status: string }) => {
    const styles: Record<string, string> = {
      COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-100",
      PENDING: "bg-amber-50 text-amber-700 border-amber-100",
      FAILED: "bg-rose-50 text-rose-700 border-rose-100",
    };
    return (
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.PENDING}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-6 app-body p-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-slate-900 flex items-center justify-center text-white shadow-sm">
            <Wallet size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              Hotspot Payments
            </h1>
            <p className="text-sm text-slate-500">
              Track all WiFi revenue and M-Pesa transactions.
            </p>
          </div>
        </div>
        <button
          onClick={loadPayments}
          className="p-2 text-slate-500 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all"
        >
          <RefreshCcw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={16}
          />
          <input
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-slate-200 outline-none"
            placeholder="Search by Code, Phone or User..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="bg-slate-50 border-none rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-slate-200"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="COMPLETED">Completed</option>
          <option value="PENDING">Pending</option>
          <option value="FAILED">Failed</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-slate-500 text-[11px] font-bold uppercase tracking-wider">
              <th className="px-6 py-4">Transaction / Date</th>
              <th className="px-6 py-4">User & Plan</th>
              <th className="px-6 py-4">Amount</th>
              <th className="px-6 py-4 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr>
                <td colSpan={4} className="py-20 text-center text-slate-400">
                  Loading data...
                </td>
              </tr>
            ) : (
              filteredPayments.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-slate-50/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900 font-mono text-sm">
                      {p.transaction_code || "---"}
                    </div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Calendar size={12} />{" "}
                      {new Date(p.created_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-slate-700">
                      {p.user?.username}
                    </div>
                    <div className="text-xs text-slate-500">
                      {p.plan?.name} • {p.phone_number}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">
                      KES {p.amount.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HotspotPaymentsPage;
